import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useGetStaffAllListQuery } from '@/api/common';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const informationOfReferringOffice = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();

		const userInfo = useSelector(selectMyInfo);

		const defaultValues: DefaultValues<any> = {};

		const { data: staffList }: any = useGetStaffAllListQuery(
			userInfo?.currentBranch?.id || '',
			{
				refetchOnMountOrArgChange: true,
				skip: !userInfo?.currentBranch,
			}
		);

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			referringTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			referringFax: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			contactTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			othersRemarksSpecify: Yup.string().when('othersRemarks', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchOthersRemarks = methods.watch('othersRemarks');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.informationOfReferringOffice')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='referringName'
									label={t('preAdmission.nameOfReferrer')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='referringPost'
									label={t('preAdmission.post')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='referringTel'
									label={t('preAdmission.telNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='referringAgency'
									label={t('preAdmission.agency')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='referringFax'
									label={t('preAdmission.faxNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='referringOfficeAddress'
									label={t('preAdmission.officeAddress')}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.remarks')}
								</Typography>
								<SingleCheckbox
									control={methods.control}
									name='centreWillContinue'
									label={t('preAdmission.remarksText1')}
								/>
								<SingleCheckbox
									control={methods.control}
									name='noFollowUpAction'
									label={t('preAdmission.remarksText2')}
								/>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='othersRemarks'
										label={t('preAdmission.othersSpecify')}
										handleChange={() => {
											methods.setValue('othersRemarksSpecify', '');
											methods.clearErrors('othersRemarksSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchOthersRemarks}
										sx={{ position: 'relative', top: '-10px' }}
										variantType={'standard'}
										control={methods.control}
										name='othersRemarksSpecify'
										label={`${t('preAdmission.specify')}${
											watchOthersRemarks ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.remarksTittle')}
								</Typography>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='contactWorker'
									label={t('preAdmission.name')}
									options={staffList?.data || []}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='contactTel'
									label={t('preAdmission.telNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='contactOfficerInCharge'
									label={t('preAdmission.officerInCharge')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

informationOfReferringOffice.displayName = 'informationOfReferringOffice';

export default informationOfReferringOffice;
